import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { createWrapper } from 'next-redux-wrapper';
import { Action, combineReducers, configureStore, Middleware, ThunkAction } from '@reduxjs/toolkit';

import { parse, stringify } from 'superjson';

import { SliceNameEnum } from '@app/enums';
import { environment } from '@app/environments/environment';

import { modalsMiddleware, searchUrlMiddleware } from './middlewares';
import {
    accommodationRatingsSlice,
    accommodationSlice,
    accommodationStaticSlice,
    checkoutDetailsSlice,
    checkoutReservationSlice,
    cmsGlobalSlice,
    cmsHomepageSlice,
    commonUiStateSlice,
    createPaymentSlice,
    homepageReducer,
    listingStateSlice,
    mapAccommodationSlice,
    mapStateSlice,
    searchStateSlice,
    summarySlice,
    userProfileSlice,
} from './slices';

const reducers = combineReducers({
    [SliceNameEnum.Accommodation]: accommodationSlice.slice.reducer,
    [SliceNameEnum.AccommodationRatings]: accommodationRatingsSlice.slice.reducer,
    [SliceNameEnum.AccommodationStatic]: accommodationStaticSlice.slice.reducer,
    [SliceNameEnum.CheckoutDetails]: checkoutDetailsSlice.slice.reducer,
    [SliceNameEnum.CheckoutReservation]: checkoutReservationSlice.slice.reducer,
    [SliceNameEnum.CmsGlobal]: cmsGlobalSlice.slice.reducer,
    [SliceNameEnum.CmsHomepage]: cmsHomepageSlice.cmsHomepageReducer,
    [SliceNameEnum.CommonUiState]: commonUiStateSlice.uiStateSlice.reducer,
    [SliceNameEnum.CreatePayment]: createPaymentSlice.slice.reducer,
    [SliceNameEnum.Homepage]: homepageReducer,
    [SliceNameEnum.Listing]: listingStateSlice.listingStateSlice.reducer,
    [SliceNameEnum.Map]: mapStateSlice.mapStateSlice.reducer,
    [SliceNameEnum.MapAccommodation]: mapAccommodationSlice.slice.reducer,
    [SliceNameEnum.ReservationDetails]: summarySlice.slice.reducer,
    [SliceNameEnum.Search]: searchStateSlice.searchStateSlice.reducer,
    [SliceNameEnum.UserProfile]: userProfileSlice.slice.reducer,
});

export type RootState = ReturnType<typeof reducers>;

const makeStore = ({
    preloadedState = undefined,
    reduxWrapperMiddleware = undefined,
}: {
    preloadedState?: Partial<RootState>;
    reduxWrapperMiddleware?: Middleware;
}) =>
    configureStore({
        preloadedState: preloadedState ?? undefined,
        devTools: environment.REDUX_DEV_TOOLS
            ? {
                  name: process.env.STORE_NAME ?? 'NOCOWANIE-STORE',
              }
            : false,
        reducer: reducers,
        middleware: getDefaultMiddleware =>
            reduxWrapperMiddleware
                ? getDefaultMiddleware()
                      .concat(reduxWrapperMiddleware)
                      .concat(modalsMiddleware, searchUrlMiddleware)
                : getDefaultMiddleware().concat(modalsMiddleware, searchUrlMiddleware),
    });

type AppStore = ReturnType<typeof makeStore>;
type AppState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];
export interface ActionWithPayload extends Action {
    type: string;
    payload?: any;
}
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, AppState, unknown, Action>;

const useAppDispatch: () => AppDispatch = useDispatch;
const useAppSelector: TypedUseSelectorHook<AppState> = useSelector;
const nocowanieAppStateWrapper = createWrapper<AppStore>(makeStore, {
    debug: false,
    serialize: actions =>
        actions.map((action: ActionWithPayload) => ({
            ...action,
            payload: stringify(action.payload),
        })),
    deserialize: actions =>
        actions.map((action: ActionWithPayload) => ({ ...action, payload: parse(action.payload) })),
});

export { reducers, makeStore, useAppDispatch, useAppSelector, nocowanieAppStateWrapper };
export type { AppStore, AppState };
